import { LevelPanel, StatsPanel } from '@repo/ui'
import { DailyCheckInPanel } from 'src/features/dailyCheckIn'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import {
  useGetPlayerLevel,
  useGetPlayerStats,
  useIsLevelSystemEnabled,
  getNextLevelInfo
} from 'src/store/playerLevel'
import { useGetCurrentPlayer } from 'src/store/player'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'

const LEVEL_BG_IMAGE = '/assets/bg/level.webp'
const STATS_BG_IMAGE = '/assets/bg/stats.webp'

export function PlayerPanel() {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const player = useGetCurrentPlayer()
  const playerLevel = useGetPlayerLevel()
  const playerStats = useGetPlayerStats()
  const levelSystemEnabled = useIsLevelSystemEnabled()

  // Get level tier info for display
  const nextTier = playerLevel ? getNextLevelInfo(playerLevel.level) : null
  const isMaxLevel = playerLevel?.level === 10

  // Get currency from player's balance (always available for authenticated players)
  const currency = isAuthenticatedPlayer(player) ? player.balance.currency : undefined

  // For USD players, we can use XP directly as totalWagered (XP = TotalWageredUSD in cents)
  // For non-USD players, we need the backend-converted value
  const totalWagered =
    playerStats?.totalWagered ?? (currency === 'USD' ? playerLevel?.xp : undefined)

  // For totalWon and biggestWin, we always need the backend value (either already converted or USD)
  const totalWon = playerStats?.totalWon
  const biggestWin = playerStats?.biggestWin

  // Stats panel translations
  const statsTranslations = {
    title: t('Stats'),
    subtitle: t('Your lifetime betting statistics'),
    tooltip: t('Track your betting performance'),
    wagered: t('Wagered'),
    totalBets: t('Total Bets'),
    won: t('Won'),
    notAvailable: t('Not available for play money'),
    approximate: t('Approximate total — see Bet History for exact amounts')
  }

  // Level panel translations
  const levelTranslations = {
    title: t('Level Boost'),
    subtitle: t('Wager to earn XP and unlock multiplier boosts'),
    tooltip: t('Earn XP by wagering. Higher levels unlock permanent multiplier boosts!'),
    yourLevel: t('Your level:'),
    maxLevel: t('MAX LEVEL'),
    multiplierBoost: t('Multiplier boost:'),
    level: t('Level')
  }

  return (
    <div className="player-sidebar">
      {levelSystemEnabled && playerStats && (
        <StatsPanel
          totalBets={playerStats.totalBets}
          totalWins={playerStats.totalWins}
          winRate={playerStats.winRate}
          totalWagered={totalWagered}
          totalWon={totalWon}
          biggestWin={biggestWin}
          currency={currency}
          bgImageUrl={STATS_BG_IMAGE}
          className="mb-4"
          translations={statsTranslations}
        />
      )}
      {levelSystemEnabled && playerLevel && (
        <LevelPanel
          level={playerLevel.level}
          currentXP={playerLevel.xp}
          nextLevelXP={nextTier?.minXP ?? playerLevel.xp}
          multiplierBonus={playerLevel.multiplierBonus}
          nextLevelBonus={nextTier?.multiplierBonus ?? playerLevel.multiplierBonus}
          isMaxLevel={isMaxLevel}
          bgImageUrl={LEVEL_BG_IMAGE}
          className="mb-4"
          translations={levelTranslations}
        />
      )}
      <DailyCheckInPanel />
    </div>
  )
}
